import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";

const EMPTY = { textbook_title: "", edition: "", subject: "", glossary_url: "", notes: "", requester_name: "", requester_email: "", requester_role: "student" };
const FIELDS = [
  ["textbook_title", "Textbook title *", "e.g. Nursing Pharmacology"],
  ["edition", "Edition", "e.g. 2nd edition (2e)"],
  ["subject", "Subject or program", "e.g. Nursing, Law, Welding"],
  ["glossary_url", "Link to the glossary (if you have it)", "https://…"],
];
const ROLES = [["student", "Student"], ["instructor", "Instructor"], ["other", "Other"]];

export const STATUS_STYLE = {
  Open: "bg-slate-100 text-slate-700",
  Planned: "bg-sky-100 text-sky-800",
  Added: "bg-emerald-100 text-emerald-800",
  Declined: "bg-stone-200 text-stone-600",
};

export const PackRequestDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => { if (open) { setForm(EMPTY); setDone(false); } }, [open]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/pack-requests", form);
      setDone(true);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pack-request-dialog" className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl font-extrabold tracking-tight">Request a textbook</DialogTitle>
          <DialogDescription>Tell us which textbook glossary you'd like as a Gloss Pack. No account needed — leave an email if you want to hear when it's ready.</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-900" data-testid="pack-request-success">
            <b>Request sent — thank you!</b> We add packs one textbook at a time, straight from the official glossary, so every definition stays word for word.
          </div>
        ) : (
          <div className="space-y-4">
            {FIELDS.map(([k, label, ph]) => (
              <div key={k} className="space-y-1.5">
                <Label>{label}</Label>
                <Input data-testid={`pack-request-${k.replace(/_/g, "-")}-input`} placeholder={ph} value={form[k]} onChange={set(k)} />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label>I am a…</Label>
              <div className="flex gap-2">
                {ROLES.map(([v, label]) => (
                  <button key={v} type="button" data-testid={`pack-request-role-${v}`} onClick={() => setForm({ ...form, requester_role: v })}
                    className={`rounded-full border px-3.5 py-1 text-sm ${form.requester_role === v ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white text-slate-700"}`}>{label}</button>
                ))}
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Your name (optional)</Label><Input data-testid="pack-request-requester-name-input" value={form.requester_name} onChange={set("requester_name")} /></div>
              <div className="space-y-1.5"><Label>Email (optional)</Label><Input data-testid="pack-request-requester-email-input" type="email" value={form.requester_email} onChange={set("requester_email")} /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea data-testid="pack-request-notes-input" rows={3} placeholder="Course, school, anything that helps" value={form.notes} onChange={set("notes")} />
            </div>
          </div>
        )}
        <DialogFooter>
          {done ? <Button data-testid="pack-request-close-button" onClick={() => onOpenChange(false)} className="rounded-lg">Close</Button> : (
            <Button data-testid="pack-request-submit-button" disabled={busy || !form.textbook_title.trim()} onClick={send} className="rounded-lg px-6">
              {busy ? "Sending…" : "Send request"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
